import React, { useState, useEffect } from "react";
import { Autocomplete, FormControl, FormLabel, CircularProgress } from "@mui/joy";
import { getAllEmployers, getEmployerPositions } from "../../../apiconfig/api";

interface EmployerPositionOption {
  id: string;
  label: string;
  employerName: string;
  positionName: string;
}

interface EmployerPositionSelectorProps {
  selectedPositions: string[];
  onSelectPosition: (positions: string[]) => void;
}

const EmployerPositionSelector: React.FC<EmployerPositionSelectorProps> = ({
  selectedPositions,
  onSelectPosition,
}) => {
  const [options, setOptions] = useState<EmployerPositionOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmployerPositions = async () => {
      try {
        setLoading(true);
        
        // Fetch all employers
        const employersResponse = await getAllEmployers({ per_page: 1000 });
        const employers = employersResponse.data?.employers || [];

        // Fetch positions for each employer
        const allPositions: EmployerPositionOption[] = [];
        
        for (const employer of employers) {
          try {
            const positionsResponse = await getEmployerPositions(
              employer.uuid,
              { per_page: 1000 }
            );
            const positions = positionsResponse.data?.employerPositions || [];
            
            positions.forEach((position: any) => {
              allPositions.push({
                id: position.id,
                label: `${employer.companyName} - ${position.displayNameOverride}`,
                employerName: employer.companyName,
                positionName: position.displayNameOverride,
              });
            });
          } catch (error) {
            console.error(`Error fetching positions for employer ${employer.uuid}:`, error);
          }
        }

        // Sort by employer name, then by position name
        allPositions.sort((a, b) => {
          const employerCompare = a.employerName.localeCompare(b.employerName);
          if (employerCompare !== 0) return employerCompare;
          return a.positionName.localeCompare(b.positionName);
        });

        setOptions(allPositions);
      } catch (error) {
        console.error("Error fetching employer positions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployerPositions();
  }, []);

  const handleChange = (_event: any, newValue: EmployerPositionOption[]) => {
    onSelectPosition(newValue.map((option) => option.id));
  };

  return (
    <FormControl>
      <FormLabel
        sx={{
          fontSize: { xs: "16px", lg: "18px" },
          fontWeight: 500,
          color: "#1A1B1D",
          mb: { xs: "8px", lg: "12px" },
        }}
      >
        Responded to Post
      </FormLabel>
      <Autocomplete
        multiple
        options={options}
        value={options.filter((option) => selectedPositions.includes(option.id))}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        placeholder={loading ? "Loading..." : "Select posts..."}
        loading={loading}
        disabled={loading}
        sx={{
          height: "auto",
          minHeight: "52px",
          width: "100%",
          borderRadius: "12px",
          bgcolor: "#edeff3",
          border: "none",
          outline: "none",
          fontSize: "16px",
          "& .MuiAutocomplete-input": {
            minHeight: "52px",
          },
        }}
        slotProps={{
          listbox: {
            sx: {
              maxHeight: "300px",
            },
          },
        }}
        startDecorator={loading ? <CircularProgress size="sm" /> : null}
      />
    </FormControl>
  );
};

export default EmployerPositionSelector;

